// Deterministic serialization. Op hashing depends on byte-identical output across
// JS engines, so the accepted value space is deliberately narrow: null, boolean,
// safe integers, strings, arrays, plain objects. Floats, NaN, undefined and
// non-plain objects are rejected loudly rather than coerced - a silently coerced
// value would produce a hash that verifies locally and fails everywhere else.

export type CanonicalValue =
  | null
  | boolean
  | number
  | string
  | CanonicalValue[]
  | { [key: string]: CanonicalValue };

export function canonicalize(value: unknown): string {
  if (value === null) return "null";
  switch (typeof value) {
    case "boolean":
      return value ? "true" : "false";
    case "number":
      if (!Number.isSafeInteger(value)) {
        throw new Error(`canonicalize: only safe integers allowed, got ${String(value)}`);
      }
      // -0 canonicalizes to "0"
      return String(value + 0 === 0 ? 0 : value);
    case "string":
      return JSON.stringify(value);
    case "object":
      break;
    default:
      throw new Error(`canonicalize: unsupported type ${typeof value}`);
  }
  if (Array.isArray(value)) {
    return `[${value.map((v) => canonicalize(v)).join(",")}]`;
  }
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) {
    throw new Error("canonicalize: only plain objects allowed");
  }
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record).sort();
  const parts: string[] = [];
  for (const key of keys) {
    const v = record[key];
    if (v === undefined) {
      throw new Error(`canonicalize: undefined value at key "${key}"`);
    }
    parts.push(`${JSON.stringify(key)}:${canonicalize(v)}`);
  }
  return `{${parts.join(",")}}`;
}
